import { priorityName, type Label, type Member } from '@hive/shared';
import { Ban, CalendarDays, ListTree, RefreshCw } from 'lucide-react';
import { MemberAvatar } from '@/components/MemberAvatar';
import { useSprintName } from '@/cycles/sprints-context';
import { cn } from '@/lib/utils';
import type { GridTask } from '@/data/tasks';
import { useIsBlocked } from './blocked-context';
import { dueTone, formatDue } from './format';

// Small pieces shared by list rows, board cards and the drawer.

const PRIORITY_COLOUR: Record<number, string> = {
  1: 'bg-red shadow-[0_0_6px_var(--color-red)]',
  2: 'bg-orange',
  3: 'bg-cyan',
  4: 'bg-line-hot',
};

export function PriorityMark({ priority }: { priority: number }) {
  return (
    <span
      className={cn('inline-block h-3.5 w-1 shrink-0', PRIORITY_COLOUR[priority] ?? PRIORITY_COLOUR[4])}
      title={`Priority: ${priorityName(priority)}`}
      aria-label={`Priority ${priorityName(priority)}`}
      role="img"
    />
  );
}

export function DueChip({ due, today, done }: { due: string; today: string; done: boolean }) {
  const tone = dueTone(due, today, done);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-mono text-[11px] whitespace-nowrap',
        tone === 'overdue' ? 'text-red' : tone === 'soon' ? 'text-yellow' : 'text-muted',
      )}
    >
      <CalendarDays className="size-3" aria-hidden />
      {formatDue(due, today)}
    </span>
  );
}

export function LabelChip({ label, onRemove }: { label: Label; onRemove?: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 bg-magenta/10 px-1.5 py-px font-mono text-[10px] text-magenta shadow-[inset_0_0_0_1px_rgb(255_43_214/0.35)]">
      #{label.name}
      {onRemove ? (
        <button
          type="button"
          className="cursor-pointer text-muted hover:text-text"
          onClick={onRemove}
          aria-label={`Remove label ${label.name}`}
        >
          ×
        </button>
      ) : null}
    </span>
  );
}

export function AssigneeAvatar({ member }: { member: Member | undefined }) {
  if (!member) return null;
  return (
    <span title={member.display_name} className="shrink-0">
      <MemberAvatar name={member.display_name} avatarUrl={member.avatar_url} className="size-5 text-[9px]" />
    </span>
  );
}

/** The labels, subtask count, due date and assignee for one task. */
export function TaskMeta({
  task,
  labels,
  members,
  subtaskCount,
  today,
}: {
  task: GridTask;
  labels: readonly Label[];
  members: readonly Member[];
  subtaskCount: number;
  today: string;
}) {
  const taskLabels = labels.filter((l) => task.label_ids.includes(l.id));
  const sprintName = useSprintName()(task.parent_id ? null : task.sprint_id);
  const blocked = useIsBlocked(task.id);
  return (
    <>
      {blocked && task.status !== 'done' ? (
        <span
          className="inline-flex items-center gap-1 font-mono text-[11px] text-red"
          title="Waiting on a task that isn't done"
        >
          <Ban className="size-3" aria-hidden />
          blocked
        </span>
      ) : null}
      {sprintName ? (
        <span className="inline-flex items-center gap-1 font-mono text-[11px] text-green" title={`In ${sprintName}`}>
          <RefreshCw className="size-3" aria-hidden />
          {sprintName}
        </span>
      ) : null}
      {taskLabels.map((l) => (
        <LabelChip key={l.id} label={l} />
      ))}
      {subtaskCount > 0 ? (
        <span
          className="inline-flex items-center gap-1 font-mono text-[11px] text-muted"
          title={`${subtaskCount} subtasks`}
        >
          <ListTree className="size-3" aria-hidden />
          {subtaskCount}
        </span>
      ) : null}
      {task.due_date ? <DueChip due={task.due_date} today={today} done={task.status === 'done'} /> : null}
      <AssigneeAvatar member={members.find((m) => m.id === task.assignee_id)} />
    </>
  );
}
